import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { BookOpen, Plus, Trash2, Eye, Save, X, ExternalLink } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import DoctorShell from "@/components/DoctorShell";
import RepublishBanner, { notifyPendingRepublishChanged } from "@/components/RepublishBanner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

function statusChip(s) {
    if (s === "published") return "ddi-chip ddi-chip-success";
    return "ddi-chip ddi-chip-neutral";
}

const EMPTY = { title: "", subtitle: "", body_markdown: "", tags: [], cover_image_url: "", meta_description: "", status: "draft" };

export default function Articles() {
    const [doctor, setDoctor] = useState(null);
    const [items, setItems] = useState([]);
    const [preview, setPreview] = useState(null);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [tagInput, setTagInput] = useState("");
    const [saving, setSaving] = useState(false);

    async function load() {
        try {
            const [dr, ar, pv] = await Promise.all([
                api.get("/me/doctor"),
                api.get("/me/articles"),
                api.get("/publish/preview-url").catch(() => ({ data: null })),
            ]);
            setDoctor(dr.data);
            setItems(ar.data || []);
            setPreview(pv.data);
        } catch (e) { toast.error(errMsg(e, "Failed")); }
    }
    useEffect(() => { load(); }, []);

    function startNew() { setEditing("new"); setForm({ ...EMPTY }); }
    function startEdit(a) { setEditing(a.id); setForm({ title: a.title || "", subtitle: a.subtitle || "", body_markdown: a.body_markdown || "", tags: a.tags || [], cover_image_url: a.cover_image_url || "", meta_description: a.meta_description || "", status: a.status || "draft" }); }
    function cancel() { setEditing(null); setForm(EMPTY); }

    async function save() {
        if (!form.title.trim()) { toast.error("Title required"); return; }
        setSaving(true);
        try {
            if (editing === "new") {
                await api.post("/me/articles", form);
                toast.success(form.status === "published" ? "Published draft (republish profile to make it live)" : "Draft saved");
            } else {
                await api.put(`/me/articles/${editing}`, form);
                toast.success("Saved");
            }
            await load();
            cancel();
            notifyPendingRepublishChanged();
        } catch (e) { toast.error(errMsg(e, "Failed")); }
        finally { setSaving(false); }
    }

    async function publish(a) {
        try {
            await api.put(`/me/articles/${a.id}`, { status: "published" });
            toast.success("Article marked published — republish your profile to make it live on your site");
            await load();
            notifyPendingRepublishChanged();
        } catch (e) { toast.error(errMsg(e, "Failed")); }
    }
    async function unpublish(a) {
        try {
            await api.put(`/me/articles/${a.id}`, { status: "draft" });
            toast.success("Reverted to draft");
            await load();
            notifyPendingRepublishChanged();
        } catch (e) { toast.error(errMsg(e, "Failed")); }
    }
    async function del(a) {
        if (!window.confirm(`Delete "${a.title}"? This cannot be undone.`)) return;
        try {
            await api.delete(`/me/articles/${a.id}`);
            toast.success("Deleted");
            await load();
            notifyPendingRepublishChanged();
        } catch (e) { toast.error(errMsg(e, "Failed")); }
    }
    function addTag() {
        const t = tagInput.trim();
        if (!t) return;
        if ((form.tags || []).length >= 10) { toast.error("Max 10 tags"); return; }
        if ((form.tags || []).includes(t)) { setTagInput(""); return; }
        setForm({ ...form, tags: [...(form.tags || []), t] });
        setTagInput("");
    }

    const pubBase = preview?.public_url?.replace(/\/dr\/.*$/, "") || "";

    return (
        <DoctorShell doctor={doctor}>
            <div className="mb-6 flex items-start justify-between flex-wrap gap-3" data-testid="articles-header">
                <div>
                    <h1 className="ddi-section-title">Health Articles</h1>
                    <p className="ddi-section-sub">Write patient-friendly educational articles. Each published article gets its own SEO-friendly URL and appears on your profile.</p>
                </div>
                {editing === null && (
                    <Button onClick={startNew} className="bg-[#3A80F7] hover:bg-[#2D6BE0]" data-testid="articles-new-btn">
                        <Plus size={16} className="mr-2" /> New article
                    </Button>
                )}
            </div>

            <RepublishBanner />

            {editing !== null && (
                <Card className="border-[#E5E9F2] ddi-shadow-sm mb-6" data-testid="article-editor">
                    <CardHeader className="pb-3 flex flex-row items-center justify-between">
                        <CardTitle className="text-base">{editing === "new" ? "New article" : "Edit article"}</CardTitle>
                        <button onClick={cancel} aria-label="Cancel edit" className="text-[#64748B] hover:text-[#DC2626]"><X size={16} /></button>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div>
                            <label className="text-[11px] font-semibold uppercase tracking-widest text-[#64748B] mb-1.5 block">Title <span className="text-[#DC2626]">*</span></label>
                            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} maxLength={200} data-testid="article-title" />
                        </div>
                        <div>
                            <label className="text-[11px] font-semibold uppercase tracking-widest text-[#64748B] mb-1.5 block">Subtitle</label>
                            <Input value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} maxLength={400} data-testid="article-subtitle" />
                        </div>
                        <div>
                            <label className="text-[11px] font-semibold uppercase tracking-widest text-[#64748B] mb-1.5 block">Cover image URL (optional)</label>
                            <Input value={form.cover_image_url} onChange={(e) => setForm({ ...form, cover_image_url: e.target.value })} placeholder="/api/uploads/..." data-testid="article-cover" />
                            <div className="text-[11px] text-[#94A3B8] mt-1">Tip: upload via Media Gallery, then paste the URL here.</div>
                        </div>
                        <div>
                            <label className="text-[11px] font-semibold uppercase tracking-widest text-[#64748B] mb-1.5 block">Body (Markdown)</label>
                            <Textarea rows={14} value={form.body_markdown} onChange={(e) => setForm({ ...form, body_markdown: e.target.value })} placeholder="## Section\nWrite your article here. Use **bold**, *italic*, [links](https://...), - lists, and ## headings." className="font-mono text-[13px]" data-testid="article-body" />
                        </div>
                        <div>
                            <label className="text-[11px] font-semibold uppercase tracking-widest text-[#64748B] mb-1.5 block">Meta description (SEO)</label>
                            <Textarea rows={2} value={form.meta_description} onChange={(e) => setForm({ ...form, meta_description: e.target.value })} maxLength={200} data-testid="article-meta" />
                        </div>
                        <div>
                            <label className="text-[11px] font-semibold uppercase tracking-widest text-[#64748B] mb-1.5 block">Tags</label>
                            <div className="flex gap-2 mb-2">
                                <Input value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); }}} placeholder="Add tag and press Enter" data-testid="article-tag-input" />
                                <Button onClick={addTag} className="bg-[#3A80F7] hover:bg-[#2D6BE0]">Add</Button>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {(form.tags || []).map((t) => (
                                    <span key={t} className="ddi-chip ddi-chip-primary" data-testid={`article-tag-${t}`}>
                                        {t}
                                        <button onClick={() => setForm({ ...form, tags: form.tags.filter((x) => x !== t) })} className="ml-1 hover:text-[#DC2626]"><X size={11} /></button>
                                    </span>
                                ))}
                            </div>
                        </div>
                        <div className="flex items-center gap-3 pt-2">
                            <label className="text-[13px] text-[#0F172A] flex items-center gap-2">
                                <input type="checkbox" checked={form.status === "published"} onChange={(e) => setForm({ ...form, status: e.target.checked ? "published" : "draft" })} data-testid="article-publish-check" />
                                Mark as published (visible on your profile after next republish)
                            </label>
                            <div className="ml-auto flex gap-2">
                                <Button variant="outline" onClick={cancel}>Cancel</Button>
                                <Button onClick={save} disabled={saving} className="bg-[#3A80F7] hover:bg-[#2D6BE0]" data-testid="article-save-btn">
                                    <Save size={14} className="mr-2" /> {saving ? "Saving…" : "Save"}
                                </Button>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Article list */}
            {items.length === 0 && editing === null ? (
                <Card className="border-[#E5E9F2] border-dashed">
                    <CardContent className="py-16 text-center">
                        <BookOpen size={40} className="mx-auto text-[#94A3B8] mb-3" />
                        <div className="text-[15px] font-semibold text-[#0F172A]">No articles yet</div>
                        <div className="text-[13px] text-[#64748B] mt-1">Write patient-friendly content to build trust and improve SEO.</div>
                    </CardContent>
                </Card>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {items.map((a) => {
                        const url = doctor?.slug && a.status === "published" ? `${pubBase}/dr/${doctor.slug}/articles/${a.slug}` : null;
                        return (
                            <Card key={a.id} className="border-[#E5E9F2] ddi-shadow-sm hover:border-[#3A80F7] transition-colors" data-testid={`article-${a.id}`}>
                                {a.cover_image_url && (
                                    <div className="h-40 bg-[#F5F7FB] rounded-t-xl overflow-hidden">
                                        <img src={a.cover_image_url.startsWith("http") ? a.cover_image_url : `${process.env.REACT_APP_BACKEND_URL}${a.cover_image_url}`} alt="" className="w-full h-full object-cover" />
                                    </div>
                                )}
                                <CardContent className="pt-4">
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className={statusChip(a.status)}>{a.status}</span>
                                        {(a.tags || []).slice(0, 2).map((t) => <span key={t} className="ddi-chip ddi-chip-neutral">{t}</span>)}
                                    </div>
                                    <div className="text-[15px] font-semibold text-[#0F172A] line-clamp-2 leading-snug mb-1">{a.title}</div>
                                    <div className="text-[12px] text-[#64748B] line-clamp-2 mb-3">{a.subtitle || a.meta_description || <span className="italic">No summary</span>}</div>
                                    <div className="flex items-center gap-2 flex-wrap text-[12px]">
                                        <button onClick={() => startEdit(a)} className="text-[#3A80F7] hover:underline" data-testid={`article-edit-${a.id}`}>Edit</button>
                                        {a.status === "draft" ? (
                                            <button onClick={() => publish(a)} className="text-[#16A34A] hover:underline" data-testid={`article-publish-${a.id}`}>Publish</button>
                                        ) : (
                                            <button onClick={() => unpublish(a)} className="text-[#F59E0B] hover:underline" data-testid={`article-unpublish-${a.id}`}>Unpublish</button>
                                        )}
                                        {url && <a href={url} target="_blank" rel="noopener" className="text-[#3A80F7] hover:underline inline-flex items-center gap-1"><ExternalLink size={11} /> View</a>}
                                        <button onClick={() => del(a)} className="ml-auto text-[#DC2626] hover:underline" data-testid={`article-del-${a.id}`}><Trash2 size={12} /></button>
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            )}
        </DoctorShell>
    );
}
